import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Building2, Check, CreditCard, Phone, ArrowLeft, Sparkles, Crown, Zap, Clock, Shield, Loader2 } from 'lucide-react';
import { getSubscriptionPlans } from '../mock';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

// Get API URL - use environment variable or fallback to current origin for deployed apps
const getApiUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (envUrl && envUrl.trim() !== '') {
    return envUrl;
  }
  // Fallback: use current origin (for deployed apps where env might not be set)
  return window.location.origin;
};
const API_URL = getApiUrl();

// Plan hierarchy for upgrade/downgrade validation (lower index = lower tier)
const PLAN_HIERARCHY = {
  'PLAN_TRIAL': 0,
  'PLAN_FREE_TRIAL': 0,
  'PLAN_1_MONTH': 1,
  'PLAN_3_MONTHS': 2,
  'PLAN_6_MONTHS': 3,
  'PLAN_12_MONTHS': 4,
  'PLAN_5_YEARS': 5,
  'PLAN_LIFETIME': 5
};

const ChoosePlan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, login } = useAuth();
  const { toast } = useToast();
  const email = location.state?.email || currentUser?.email;
  const isUpgrade = location.state?.upgrade;
  const isRenewal = location.state?.renewal;
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentDetails, setPaymentDetails] = useState('');
  const plans = getSubscriptionPlans();

  // Check if the new plan is a downgrade from current plan
  const isDowngrade = (newPlanId) => {
    const currentPlan = currentUser?.subscription?.plan;
    if (!currentPlan) return false;
    
    const currentTier = PLAN_HIERARCHY[currentPlan] ?? -1;
    const newTier = PLAN_HIERARCHY[newPlanId] ?? -1;
    
    return newTier < currentTier;
  };

  // Check if user has an active (non-expired) subscription
  const hasActiveSubscription = () => {
    if (!currentUser?.subscription) return false;
    
    const { status, expiryDate, plan } = currentUser.subscription;
    
    // Lifetime plan is always active
    if (plan === 'PLAN_LIFETIME') return true;
    
    // Check if subscription is active and not expired
    if (status !== 'active') return false;
    
    if (expiryDate) {
      const expiry = new Date(expiryDate);
      const now = new Date();
      return expiry > now;
    }
    
    return false;
  };

  const handleBack = () => {
    if (currentUser) {
      navigate('/profile');
    } else {
      navigate('/login');
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.crossOrigin = 'anonymous';
      
      // Add timeout for script loading
      const loadTimeout = setTimeout(() => {
        console.error('Razorpay script load timeout');
        resolve(false);
      }, 10000); // 10 second timeout
      
      script.onload = () => {
        clearTimeout(loadTimeout);
        // Double check Razorpay is available
        setTimeout(() => {
          if (window.Razorpay) {
            resolve(true);
          } else {
            console.error('Razorpay loaded but constructor not available');
            resolve(false);
          }
        }, 100);
      };
      script.onerror = (error) => {
        clearTimeout(loadTimeout);
        console.error('Failed to load Razorpay script:', error);
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const handleFreeTrial = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/subscriptions/activate-trial`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to activate trial');
      }

      toast({
        title: 'Trial Activated! 🎉',
        description: 'Your 15-day free trial is now active. Enjoy all features!'
      });
      
      // Refresh user data
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Failed to activate trial:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to activate free trial',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRazorpayPayment = async (planId) => {
    setLoading(true);
    
    // Set a timeout to reset loading if something goes wrong
    const loadingTimeout = setTimeout(() => {
      setLoading(false);
      toast({
        title: 'Request Timeout',
        description: 'Payment gateway took too long. Please try again.',
        variant: 'destructive'
      });
    }, 30000); // 30 second timeout
    
    try {
      
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        clearTimeout(loadingTimeout);
        toast({
          title: 'Payment Gateway Error',
          description: 'Failed to load Razorpay. Please check your internet connection or try a different browser.',
          variant: 'destructive'
        });
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      
      // Debug: Check if token exists
      if (!token) {
        clearTimeout(loadingTimeout);
        toast({
          title: 'Session Expired',
          description: 'Please login again to continue with the purchase.',
          variant: 'destructive'
        });
        setLoading(false);
        return;
      }

      
      // Create order with timeout
      const controller = new AbortController();
      const fetchTimeout = setTimeout(() => controller.abort(), 15000);
      
      const createOrderUrl = `${API_URL}/api/subscriptions/create-order?plan_id=${planId}`;
      
      const orderResponse = await fetch(createOrderUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });
      
      clearTimeout(fetchTimeout);

      if (!orderResponse.ok) {
        const error = await orderResponse.json();
        console.error('Order creation failed:', error);
        if (error.detail?.includes('not configured')) {
          // Razorpay not configured, show manual payment option
          clearTimeout(loadingTimeout);
          setSelectedPlan(planId);
          setManualDialogOpen(true);
          setLoading(false);
          return;
        }
        throw new Error(error.detail || 'Failed to create order');
      }

      const orderData = await orderResponse.json();
      
      if (!orderData.orderId || !orderData.keyId) {
        throw new Error('Invalid order response from server');
      }

      // Open Razorpay checkout
      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'MyVenueMate',
        description: `${orderData.planName} Subscription`,
        order_id: orderData.orderId,
        handler: async (response) => {
          // Verify payment
          try {
            const verifyResponse = await fetch(`${API_URL}/api/subscriptions/verify-payment?razorpay_order_id=${response.razorpay_order_id}&razorpay_payment_id=${response.razorpay_payment_id}&razorpay_signature=${response.razorpay_signature}`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            if (!verifyResponse.ok) {
              throw new Error('Payment verification failed');
            }

            toast({
              title: 'Payment Successful! 🎉',
              description: 'Your subscription has been activated.'
            });
            
            window.location.href = '/dashboard';
          } catch (err) {
            toast({
              title: 'Verification Failed',
              description: 'Please contact support with your payment ID: ' + response.razorpay_payment_id,
              variant: 'destructive'
            });
          }
        },
        prefill: {
          name: currentUser?.name || '',
          email: currentUser?.email || email,
          contact: currentUser?.phone?.replace(/\D/g, '') || ''
        },
        theme: {
          color: '#0d9488'
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
          }
        }
      };

      
      // Clear the main timeout since we're about to open the modal
      clearTimeout(loadingTimeout);
      
      // Check if Razorpay is available
      if (!window.Razorpay) {
        throw new Error('Razorpay not loaded. Please refresh the page and try again.');
      }
      
      const razorpay = new window.Razorpay(options);
      
      razorpay.on('payment.failed', function (response) {
        console.error('Payment failed:', response.error);
        toast({
          title: 'Payment Failed',
          description: response.error?.description || 'Payment was not successful. Please try again.',
          variant: 'destructive'
        });
        setLoading(false);
      });
      
      try {
        razorpay.open();
      } catch (openError) {
        console.error('Failed to open Razorpay modal:', openError);
        throw new Error('Failed to open payment window. Please try again or use manual payment option.');
      }
      
      // Reset loading after modal opens (modal handles its own state)
      setLoading(false);
      
    } catch (error) {
      clearTimeout(loadingTimeout);
      console.error('Payment error:', error);
      
      let errorMessage = 'Failed to process payment. Please try again.';
      if (error.name === 'AbortError') {
        errorMessage = 'Request timed out. Please check your internet connection.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Payment Error',
        description: errorMessage,
        variant: 'destructive'
      });
      setLoading(false);
    }
  };

  const handleManualApproval = async () => {
    if (!paymentRef.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter payment reference/transaction ID',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/subscriptions/request-manual-approval?plan_id=${selectedPlan}&payment_reference=${encodeURIComponent(paymentRef)}&payment_details=${encodeURIComponent(paymentDetails)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit request');
      }

      toast({
        title: 'Request Submitted! 📩',
        description: 'Your subscription request has been sent for approval. You will be notified once approved.'
      });
      
      setManualDialogOpen(false);
      navigate('/subscription-pending');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit request',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChoosePlan = (planId) => {
    // Check for downgrade attempt (only for non-master users with active subscription)
    if (!currentUser?.isMaster && hasActiveSubscription() && isDowngrade(planId)) {
      const currentPlanName = plans.find(p => p.id === currentUser?.subscription?.plan)?.name || currentUser?.subscription?.plan;
      const newPlanName = plans.find(p => p.id === planId)?.name || planId;
      
      toast({
        title: 'Downgrade Not Allowed',
        description: `You cannot downgrade from "${currentPlanName}" to "${newPlanName}" while your current subscription is active. Please wait until your subscription expires or contact support.`,
        variant: 'destructive'
      });
      return;
    }
    
    if (planId === 'PLAN_TRIAL') {
      handleFreeTrial();
    } else {
      handleRazorpayPayment(planId);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'PLAN_TRIAL': return <Clock className="w-6 h-6" />;
      case 'PLAN_1_MONTH': return <Zap className="w-6 h-6" />;
      case 'PLAN_3_MONTHS': return <Sparkles className="w-6 h-6" />;
      case 'PLAN_6_MONTHS': return <Shield className="w-6 h-6" />;
      case 'PLAN_12_MONTHS': return <Crown className="w-6 h-6" />;
      case 'PLAN_LIFETIME': return <Crown className="w-6 h-6" />;
      default: return <Zap className="w-6 h-6" />;
    }
  };

  const getBadgeStyle = (badge) => {
    switch (badge) {
      case 'FREE': return 'bg-green-600 text-white';
      case 'POPULAR': return 'bg-orange-500 text-white';
      case 'BEST VALUE': return 'bg-teal-600 text-white';
      case 'LIFETIME': return 'bg-purple-600 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-orange-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 p-4 py-8">
      <div className="w-full max-w-7xl mx-auto">
        {/* Back Button */}
        {currentUser && (
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="text-teal-700 dark:text-teal-400 hover:text-teal-900 dark:hover:text-teal-300 font-medium"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        )}

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl mb-4 shadow-lg">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
            {isUpgrade ? 'Upgrade Your Plan' : isRenewal ? 'Renew Subscription' : 'Choose Your Plan'}
          </h1>
          <p className="text-gray-700 dark:text-gray-300 mt-2 max-w-xl mx-auto">
            All plans include full access to all features. Choose the duration that works best for you.
          </p>
          
          {/* Current Subscription Info */}
          {currentUser?.subscription?.plan === 'PLAN_LIFETIME' ? (
            <div className="mt-4 inline-flex items-center gap-2 bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-purple-900/40 dark:to-indigo-900/40 border border-purple-300 dark:border-purple-700 rounded-full px-4 py-2">
              <Crown className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              <span className="text-sm text-purple-900 dark:text-purple-200">
                <strong>You have a 5 Years subscription!</strong>
                <span className="ml-2 text-purple-700 dark:text-purple-300">
                  • Enjoy unlimited access for 5 years
                </span>
              </span>
            </div>
          ) : currentUser?.subscription?.expiryDate && (
            <div className="mt-4 inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/40 dark:to-orange-900/40 border border-amber-300 dark:border-amber-700 rounded-full px-4 py-2">
              <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              <span className="text-sm text-amber-900 dark:text-amber-200">
                <strong>Current plan expires:</strong>{' '}
                {new Date(currentUser.subscription.expiryDate).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
                {currentUser.subscription.status === 'active' && (
                  <span className="ml-2 text-green-700 dark:text-green-400">
                    • New plan will start after this date
                  </span>
                )}
              </span>
            </div>
          )}
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl ${
                plan.badge === 'BEST VALUE' ? 'ring-2 ring-teal-500 shadow-lg' : ''
              } ${plan.badge === 'LIFETIME' ? 'ring-2 ring-purple-500' : ''}`}
            >
              {plan.badge && (
                <div className={`absolute top-0 right-0 ${getBadgeStyle(plan.badge)} text-xs px-3 py-1 rounded-bl-lg font-semibold shadow-md`}>
                  {plan.badge}
                </div>
              )}
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2 text-teal-600 mb-2">
                  {getPlanIcon(plan.id)}
                  <CardTitle className="text-xl dark:text-foreground">{plan.name}</CardTitle>
                </div>
                
                {/* Price Display - Net Price Big, GST Small */}
                <div className="mt-3">
                  <span className="text-3xl font-bold text-gray-900 dark:text-foreground">
                    {plan.price === 0 ? 'FREE' : `₹${plan.price.toLocaleString('en-IN')}`}
                  </span>
                  {plan.price > 0 && (
                    <span className="text-sm text-gray-500 dark:text-muted-foreground ml-1">
                      /{plan.duration} days
                    </span>
                  )}
                </div>
                
                {/* GST Breakdown - Small Text */}
                {plan.base_price && (
                  <p className="text-[10px] text-gray-400 dark:text-muted-foreground mt-1">
                    (₹{plan.base_price.toLocaleString('en-IN')} + ₹{plan.gst_amount.toLocaleString('en-IN')} GST)
                  </p>
                )}
                
                {/* Savings Badge for 5 Years Plan */}
                {(plan.id === 'PLAN_LIFETIME' || plan.id === 'PLAN_5_YEARS') && (
                  <div className="mt-2 space-y-1">
                    <div className="inline-flex items-center px-3 py-1.5 bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-100 rounded-full text-sm font-bold">
                      💰 Save ₹82,541 vs 1 Month Plan
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      (₹2,359 × 60 months = ₹1,41,540)
                    </p>
                  </div>
                )}
                
                {/* Savings for other plans */}
                {plan.id === 'PLAN_12_MONTHS' && (
                  <div className="mt-2 inline-flex items-center px-2 py-1 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded-full text-xs font-semibold">
                    Save ₹10,609 (37% off)
                  </div>
                )}
                
                <CardDescription className="text-xs text-gray-500 dark:text-muted-foreground mt-2">
                  {plan.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-2">
                <ul className="space-y-2 mb-4">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start text-sm">
                      <Check className="w-4 h-4 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
                {/* Show downgrade warning for non-master users */}
                {!currentUser?.isMaster && hasActiveSubscription() && isDowngrade(plan.id) ? (
                  <div className="space-y-2">
                    <Button
                      disabled={true}
                      className="w-full bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white cursor-not-allowed font-semibold border border-gray-400 dark:border-gray-500"
                    >
                      Downgrade Not Allowed
                    </Button>
                    <p className="text-xs text-center text-gray-700 dark:text-gray-200 font-medium">
                      Wait until your current plan expires
                    </p>
                  </div>
                ) : (
                  // Check if this is the current plan (handle PLAN_FREE_TRIAL = PLAN_TRIAL)
                  (currentUser?.subscription?.plan === plan.id || 
                   (plan.id === 'PLAN_TRIAL' && currentUser?.subscription?.plan === 'PLAN_FREE_TRIAL')) 
                  && hasActiveSubscription()
                ) ? (
                  <Button
                    disabled={true}
                    className="w-full bg-teal-100 dark:bg-teal-700 text-teal-900 dark:text-white cursor-default font-semibold border border-teal-400 dark:border-teal-500"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Current Plan
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleChoosePlan(plan.id)}
                    disabled={loading}
                    className={`w-full ${
                      plan.badge === 'BEST VALUE'
                        ? 'bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700'
                        : plan.badge === 'LIFETIME'
                        ? 'bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700'
                        : plan.price === 0
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-gray-800 hover:bg-gray-900'
                    }`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Processing...
                      </>
                    ) : plan.price === 0 ? (
                      'Start Free Trial'
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Buy Now
                      </>
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Manual Payment Option */}
        <Card className="max-w-2xl mx-auto border-2 border-orange-300 dark:border-orange-700 bg-orange-100 dark:bg-orange-900/30">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-4">
              <Phone className="w-6 h-6 text-orange-600 dark:text-orange-400 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Paid Directly? Contact Developer</h3>
                <p className="text-sm text-gray-800 dark:text-gray-200 mb-4">
                  If you&apos;ve made a direct payment (UPI, Bank Transfer, etc.), submit your payment details for manual approval.
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedPlan('PLAN_1_MONTH');
                    setManualDialogOpen(true);
                    // Scroll to top on mobile so dialog is visible
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="border-orange-400 dark:border-orange-600 text-orange-700 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-900/50 font-medium"
                >
                  Request Manual Approval
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors font-medium"
          >
            Already have an account? Sign in
          </button>
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Powered by <span className="font-semibold text-teal-600 dark:text-teal-400">MYONK</span>
          </p>
        </div>
      </div>

      {/* Manual Approval Dialog */}
      <Dialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-orange-600" />
              Manual Payment Approval
            </DialogTitle>
            <DialogDescription>
              Submit your payment details for admin verification
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="planSelect">Select Plan</Label>
              <select
                id="planSelect"
                value={selectedPlan || ''}
                onChange={(e) => setSelectedPlan(e.target.value)}
                className="w-full border rounded-md p-2"
              >
                {plans.filter(p => p.price > 0).map(plan => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} - ₹{plan.price.toLocaleString('en-IN')}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="paymentRef">Payment Reference / Transaction ID *</Label>
              <Input
                id="paymentRef"
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
                placeholder="e.g., UPI123456789 or Bank Ref No."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="paymentDetails">Additional Details (Optional)</Label>
              <Textarea
                id="paymentDetails"
                value={paymentDetails}
                onChange={(e) => setPaymentDetails(e.target.value)}
                placeholder="Payment date, amount, payment method, etc."
                rows={3}
              />
            </div>
            <div className="bg-gray-50 dark:bg-secondary p-3 rounded-lg text-sm text-gray-600 dark:text-muted-foreground">
              <p className="font-medium mb-1">Payment Details:</p>
              <p>UPI: myvenuemate@ybl</p>
              <p>Contact: info@myvenuemate.in</p>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setManualDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleManualApproval}
              disabled={loading}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Request'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChoosePlan;
